import { Edit as EditIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { toast } from 'react-toastify';

import { SWLoadingButton } from '@/components/LoadingButton';
import { SWLoadingButtonOutlined } from '@/components/SWLoadingButtonOutlined';
import useMixPanel from '@/hooks/useMixPanel';
import { usePmsApiEnabled } from '@/hooks/usePmsApiEnabled';
import { usePmsBetaEnabled } from '@/hooks/usePmsBetaEnabled';

import type {
  AppfolioConfig,
  PmsConfig,
  PmsType,
  RentManagerConfig,
  ResmanConfig,
} from '../../types';
import { PMS_PROVIDERS } from '../../types';
import type { Unit } from '@/Units/types';
import { updateUnitPmsConfig } from '@/Units/api';
import { naturalCompare } from '@/util';

import { AppfolioConfigForm } from './AppfolioConfigForm';
import { ResmanConfigForm } from './ResmanConfigForm';
import { RmMatchingDialog } from './RmMatchingDialog';

const useStyles = makeStyles(() => ({
  editIcon: {
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  fieldLabel: {
    fontWeight: 600,
    marginBottom: '4px',
  },
  fieldValue: {
    color: '#666',
  },
  placeholder: {
    color: '#999',
    fontStyle: 'italic',
  },
}));

interface PmsSettingsProps {
  pmsType: PmsType | null;
  pmsConfig: PmsConfig | null;
  units: Unit[];
  onUpdate: (pmsType: PmsType | null, pmsConfig: PmsConfig | null) => Promise<void>;
  onRefresh?: () => void;
}

export const PmsSettings = ({
  pmsType,
  pmsConfig,
  units,
  onUpdate,
  onRefresh,
}: PmsSettingsProps): JSX.Element => {
  const classes = useStyles();
  const { trackEvent } = useMixPanel();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showRmMatching, setShowRmMatching] = React.useState(false);
  const [showClearConfirm, setShowClearConfirm] = React.useState(false);

  // Narrow pmsConfig to RentManagerConfig for RM-specific fields.
  // All existing rendering logic in this component is RM-only; TASK-006 will refactor per-provider.
  const rmConfig = pmsType === 'RENT_MANAGER' ? (pmsConfig as RentManagerConfig | null) : null;

  // Local form state (used only for CSV-only PMS edit mode)
  const [localPmsType, setLocalPmsType] = React.useState<PmsType | ''>(pmsType || '');
  const [localPropertyShortName, setLocalPropertyShortName] = React.useState(
    rmConfig?.property_short_name || '',
  );
  const [localChargeType, setLocalChargeType] = React.useState(rmConfig?.default_charge_type || '');
  const [localUnitMappings, setLocalUnitMappings] = React.useState<Record<string, string>>({});

  // Feature flag: PMS API integration
  const { pmsApiEnabled } = usePmsApiEnabled();
  // Feature flag: gates non-GA (beta) providers in the dropdown
  const { pmsBetaEnabled } = usePmsBetaEnabled();

  const visibleProviders = React.useMemo(
    () =>
      (Object.entries(PMS_PROVIDERS) as [PmsType, (typeof PMS_PROVIDERS)[PmsType]][])
        .filter(([, p]) => !p.beta || pmsBetaEnabled)
        .sort(([, a], [, b]) => naturalCompare(a.label, b.label)),
    [pmsBetaEnabled],
  );

  // Capabilities (API features gated by feature flag)
  const provider = pmsType ? PMS_PROVIDERS[pmsType] : null;
  const hasApiConfig = pmsApiEnabled && (provider?.capabilities.includes('api_config') ?? false);
  const hasCsvExport = provider?.capabilities.includes('csv_export') ?? false;
  const hasApiUpload = pmsApiEnabled && (provider?.capabilities.includes('api_upload') ?? false);
  const isApiReady = !!rmConfig?.property_id && !!rmConfig?.charge_type_id;

  // Build unit mappings from units[].pms_config.unit_name
  const buildMappingsFromUnits = React.useCallback(() => {
    const mappings: Record<string, string> = {};
    units.forEach((u) => {
      if (u.pms_config?.unit_name) {
        mappings[u.id] = u.pms_config.unit_name;
      }
    });
    return mappings;
  }, [units]);

  // Reset local state when props change
  React.useEffect(() => {
    setLocalPmsType(pmsType || '');
    setLocalPropertyShortName(rmConfig?.property_short_name || '');
    setLocalChargeType(rmConfig?.default_charge_type || '');
    setLocalUnitMappings(buildMappingsFromUnits());
    setIsEditing(false);
    // rmConfig is derived from pmsType + pmsConfig (both in deps), so rmConfig's fields are covered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmsType, pmsConfig, buildMappingsFromUnits]);

  const billableUnits = React.useMemo(
    () =>
      units
        .filter((u) => {
          const flags = (u.unit_flags ?? []) as string[];
          return !flags.includes('COMMON_AREA') && !flags.includes('NON_BILLABLE');
        })
        .sort((a, b) => naturalCompare(a.name, b.name)),
    [units],
  );

  const mappedCount = React.useMemo(
    () => billableUnits.filter((u) => u.pms_config?.unit_name).length,
    [billableUnits],
  );

  // --- CSV-only PMS edit handlers (kept for future non-api_config providers) ---

  const handleSave = async () => {
    if (localPmsType === '') {
      // Clearing PMS config
      setIsSaving(true);
      try {
        await onUpdate(null, null);
        await Promise.all(
          billableUnits
            .filter((u) => u.pms_config)
            .map((u) => updateUnitPmsConfig(u.id, null, null)),
        );
        setIsEditing(false);
        onRefresh?.();
        toast.success('PMS settings cleared', { closeButton: true });
      } catch {
        resetToProps();
      } finally {
        setIsSaving(false);
      }
      return;
    }

    if (!localPropertyShortName.trim()) {
      toast.error('Property Short Name is required', { closeButton: true });
      return;
    }

    setIsSaving(true);
    try {
      const config: RentManagerConfig = {
        property_short_name: localPropertyShortName.trim(),
        default_charge_type: localChargeType.trim() || undefined,
      };
      await onUpdate(localPmsType as PmsType, config);

      const currentMappings = buildMappingsFromUnits();
      const savePromises: Promise<void>[] = [];
      for (const unit of billableUnits) {
        const newName = localUnitMappings[unit.id] || null;
        const oldName = currentMappings[unit.id] || null;
        if (newName !== oldName) {
          savePromises.push(
            updateUnitPmsConfig(
              unit.id,
              newName ? 'RENT_MANAGER' : null,
              newName ? { unit_name: newName } : null,
            ),
          );
        }
      }
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
      }

      setIsEditing(false);
      onRefresh?.();
      toast.success('PMS settings updated successfully', { closeButton: true });
    } catch {
      resetToProps();
    } finally {
      setIsSaving(false);
    }
  };

  const resetToProps = () => {
    setLocalPmsType(pmsType || '');
    setLocalPropertyShortName(rmConfig?.property_short_name || '');
    setLocalChargeType(rmConfig?.default_charge_type || '');
    setLocalUnitMappings(buildMappingsFromUnits());
  };

  const handleCancel = () => {
    resetToProps();
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    const mappings = { ...localUnitMappings };
    billableUnits.forEach((unit) => {
      if (!mappings[unit.id]) {
        mappings[unit.id] = unit.name;
      }
    });
    setLocalUnitMappings(mappings);
    setIsEditing(true);
  };

  const handleUnitMappingChange = (unitId: string, rmName: string) => {
    setLocalUnitMappings((prev) => ({ ...prev, [unitId]: rmName }));
  };

  const handleClearPms = async () => {
    setIsSaving(true);
    try {
      await onUpdate(null, null);
      await Promise.all(
        billableUnits.filter((u) => u.pms_config).map((u) => updateUnitPmsConfig(u.id, null, null)),
      );
      onRefresh?.();
      setShowClearConfirm(false);
      toast.success('PMS settings cleared', { closeButton: true });
    } catch {
      toast.error('Failed to clear PMS settings', { closeButton: true });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Render: dispatch to per-provider config forms for non-RM providers ---
  // Hits when either (a) pmsType is already AppFolio/ResMan, or (b) user picked one in
  // the dropdown below and we're still pre-save (pmsType is null, localPmsType is set).
  const effectiveProvider: PmsType | '' = pmsType ?? (localPmsType || '');

  // Pre-save state for AppFolio/ResMan: user has picked the provider in the dropdown
  // but hasn't saved yet. We pass onCancel so the user can back out and pick a different
  // provider without first saving anything. After save, pmsType is set and onCancel is
  // not passed (the configured form has its own Clear PMS path).
  const isPreSavePick = !pmsType && !!localPmsType;
  const handleProviderPickCancel = () => setLocalPmsType('');

  const handleProviderPick = (picked: PmsType | '') => {
    setLocalPmsType(picked);
    if (picked) {
      trackEvent('PMS Provider Selected', { pmsType: picked, source: 'property_settings' });
    }
  };

  if (effectiveProvider === 'APPFOLIO') {
    return (
      <AppfolioConfigForm
        pmsConfig={pmsType === 'APPFOLIO' ? (pmsConfig as AppfolioConfig | null) : null}
        units={units}
        onUpdate={
          onUpdate as (pmsType: PmsType | null, pmsConfig: AppfolioConfig | null) => Promise<void>
        }
        onRefresh={onRefresh}
        {...(isPreSavePick && { onCancel: handleProviderPickCancel })}
      />
    );
  }

  if (effectiveProvider === 'RESMAN') {
    return (
      <ResmanConfigForm
        pmsConfig={pmsType === 'RESMAN' ? (pmsConfig as ResmanConfig | null) : null}
        units={units}
        onUpdate={
          onUpdate as (pmsType: PmsType | null, pmsConfig: ResmanConfig | null) => Promise<void>
        }
        onRefresh={onRefresh}
        {...(isPreSavePick && { onCancel: handleProviderPickCancel })}
      />
    );
  }

  // --- Render: API-config PMS (read-only summary + RmMatchingDialog) ---
  // Only show read-only view when not in manual edit mode

  if (pmsType && hasApiConfig && !isEditing) {
    return (
      <Card sx={{ mt: 2, mb: 8 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              PMS Integration
            </Typography>
            <IconButton onClick={handleStartEdit} className={classes.editIcon} size="small">
              <EditIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Property Management System integration for exporting and uploading billing data.
          </Typography>

          {/* PMS Type */}
          <Box sx={{ mb: 2 }}>
            <Typography className={classes.fieldLabel} variant="subtitle2">
              PMS Type
            </Typography>
            <Typography className={classes.fieldValue}>{provider!.label}</Typography>
          </Box>

          {/* Property */}
          <Box sx={{ mb: 2 }}>
            <Typography className={classes.fieldLabel} variant="subtitle2">
              Property
            </Typography>
            <Typography className={classes.fieldValue}>
              {rmConfig?.property_short_name || 'Not set'}
            </Typography>
          </Box>

          {/* Charge Type */}
          <Box sx={{ mb: 2 }}>
            <Typography className={classes.fieldLabel} variant="subtitle2">
              Charge Type
            </Typography>
            <Typography
              className={rmConfig?.default_charge_type ? classes.fieldValue : classes.placeholder}
            >
              {rmConfig?.default_charge_type || 'Not set'}
            </Typography>
          </Box>

          {/* Unit Mappings Count */}
          <Box sx={{ mb: 3 }}>
            <Typography className={classes.fieldLabel} variant="subtitle2">
              Unit Mappings
            </Typography>
            <Typography className={classes.fieldValue}>
              {mappedCount} of {billableUnits.length} units mapped
            </Typography>
          </Box>

          {/* Status Badges */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            {hasCsvExport && rmConfig?.property_short_name && mappedCount > 0 && (
              <Chip label="CSV Ready" color="success" size="small" variant="outlined" />
            )}
            {hasCsvExport && (!rmConfig?.property_short_name || mappedCount === 0) && (
              <Tooltip
                title={
                  !rmConfig?.property_short_name && mappedCount === 0
                    ? 'A property name and at least one unit mapping are required to export CSV'
                    : !rmConfig?.property_short_name
                    ? 'A property name is required to export CSV'
                    : 'At least one unit mapping is required to export CSV'
                }
                arrow
              >
                <Chip label="CSV Not Ready" color="default" size="small" variant="outlined" />
              </Tooltip>
            )}
            {hasApiUpload && isApiReady && (
              <Chip label="API Ready" color="success" size="small" variant="outlined" />
            )}
            {hasApiUpload && !isApiReady && (
              <Tooltip
                title={
                  !rmConfig?.property_id && !rmConfig?.charge_type_id
                    ? 'A linked property and charge type are required to upload charges directly to Rent Manager'
                    : !rmConfig?.property_id
                    ? 'A linked property is required to upload charges directly to Rent Manager'
                    : 'A charge type is required to upload charges directly to Rent Manager'
                }
                arrow
              >
                <Chip label="API Not Configured" color="default" size="small" variant="outlined" />
              </Tooltip>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={() => setShowClearConfirm(true)}
              disabled={isSaving}
            >
              Clear PMS
            </Button>
            <Button variant="contained" size="small" onClick={() => setShowRmMatching(true)}>
              Reconfigure via Rent Manager
            </Button>
          </Box>
        </CardContent>

        {/* Clear PMS Confirmation Dialog */}
        <Dialog open={showClearConfirm} onClose={() => !isSaving && setShowClearConfirm(false)}>
          <DialogTitle>Clear PMS Settings?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This will remove the Rent Manager configuration and all unit mappings for this
              property. You can reconfigure at any time.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowClearConfirm(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={handleClearPms} disabled={isSaving}>
              {isSaving ? <CircularProgress size={16} /> : 'Clear PMS'}
            </Button>
          </DialogActions>
        </Dialog>

        <RmMatchingDialog
          open={showRmMatching}
          onClose={() => setShowRmMatching(false)}
          units={units}
          onUpdate={
            onUpdate as (
              pmsType: PmsType | null,
              pmsConfig: RentManagerConfig | null,
            ) => Promise<void>
          }
          onRefresh={onRefresh}
          existingConfig={rmConfig}
        />
      </Card>
    );
  }

  // --- Render: No PMS or CSV-only PMS (editable form) ---

  // When no PMS is configured and user hasn't started editing, show setup prompt
  if (!pmsType && !isEditing) {
    return (
      <Card sx={{ mt: 2, mb: 8 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              PMS Integration
            </Typography>
          </Box>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Configure a Property Management System integration to export and upload billing data.
          </Typography>

          {/* Picker is shown only when nothing's been picked yet. Once a provider is
              chosen, the picker is replaced by that provider's setup buttons plus a
              Cancel button that resets the picker. AppFolio and ResMan early-return
              into their own ConfigForms above; this block only handles RM. */}
          {!localPmsType ? (
            <Box sx={{ mb: 3 }}>
              <Typography className={classes.fieldLabel} variant="subtitle2">
                PMS Type
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={localPmsType}
                onChange={(e) => handleProviderPick(e.target.value as PmsType | '')}
                SelectProps={{ native: true }}
              >
                <option value="">Select a PMS...</option>
                {visibleProviders.map(([type, p]) => (
                  <option key={type} value={type}>
                    {p.label}
                    {p.beta ? ' (Beta)' : ''}
                  </option>
                ))}
              </TextField>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography className={classes.fieldLabel} variant="subtitle2">
                  PMS Type
                </Typography>
                <Typography className={classes.fieldValue}>
                  {PMS_PROVIDERS[localPmsType as PmsType].label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                {/* Cancel is text variant here (low emphasis) so Configure Manually
                    (outlined, medium emphasis) and Connect to RM (contained, primary)
                    each have a distinct visual weight matching their three intent
                    levels. This is the only place across PMS forms with three buttons;
                    every other form has two and uses outlined Cancel + contained action. */}
                <Button variant="text" size="small" onClick={() => setLocalPmsType('')}>
                  Cancel
                </Button>
                <Button variant="outlined" size="small" onClick={() => setIsEditing(true)}>
                  Configure Manually
                </Button>
                {pmsApiEnabled &&
                  PMS_PROVIDERS[localPmsType as PmsType]?.capabilities.includes('api_config') && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setShowRmMatching(true)}
                    >
                      Connect to {PMS_PROVIDERS[localPmsType as PmsType].label} (Recommended)
                    </Button>
                  )}
              </Box>
            </>
          )}
        </CardContent>

        <RmMatchingDialog
          open={showRmMatching}
          onClose={() => setShowRmMatching(false)}
          units={units}
          onUpdate={
            onUpdate as (
              pmsType: PmsType | null,
              pmsConfig: RentManagerConfig | null,
            ) => Promise<void>
          }
          onRefresh={onRefresh}
          existingConfig={rmConfig}
        />
      </Card>
    );
  }

  // --- Render: CSV-only PMS editable form (existing behavior) ---

  return (
    <Card sx={{ mt: 2, mb: 8 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            PMS Export Settings
          </Typography>
          {!isEditing && (
            <IconButton
              aria-label="edit"
              onClick={handleStartEdit}
              className={classes.editIcon}
              size="small"
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Configure Property Management System integration to export billing data as a CSV that can
          be directly imported into your PMS.
        </Typography>

        {isEditing && pmsApiEnabled && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Manual configuration only supports CSV exports. To upload charges directly to Rent
            Manager, use &quot;Connect to Rent Manager&quot; instead.
          </Alert>
        )}

        {/* PMS Type */}
        <Box sx={{ mb: 3 }}>
          <Typography className={classes.fieldLabel} variant="subtitle2">
            PMS Type
          </Typography>
          {isEditing ? (
            <TextField
              select
              fullWidth
              size="small"
              value={localPmsType}
              onChange={(e) => handleProviderPick(e.target.value as PmsType | '')}
              disabled={isSaving}
              SelectProps={{ native: true }}
            >
              <option value="">None</option>
              <option value="RENT_MANAGER">Rent Manager</option>
            </TextField>
          ) : (
            <Typography className={pmsType ? classes.fieldValue : classes.placeholder}>
              {pmsType === 'RENT_MANAGER' ? 'Rent Manager' : 'Not configured'}
            </Typography>
          )}
        </Box>

        {/* Show config fields only when a PMS type is selected */}
        {(isEditing ? localPmsType !== '' : pmsType !== null) && (
          <>
            {/* Property Short Name */}
            <Box sx={{ mb: 3 }}>
              <Typography className={classes.fieldLabel} variant="subtitle2">
                Property Short Name
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  value={localPropertyShortName}
                  onChange={(e) => setLocalPropertyShortName(e.target.value)}
                  placeholder="e.g., Oak Apartments"
                  helperText="Must match the Property Short Name in Rent Manager"
                  disabled={isSaving}
                />
              ) : (
                <Typography className={classes.fieldValue}>
                  {rmConfig?.property_short_name || 'Not set'}
                </Typography>
              )}
            </Box>

            {/* Default Charge Type */}
            <Box sx={{ mb: 3 }}>
              <Typography className={classes.fieldLabel} variant="subtitle2">
                Default Charge Type
              </Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  value={localChargeType}
                  onChange={(e) => setLocalChargeType(e.target.value)}
                  placeholder="e.g., Water"
                  helperText="Optional charge type code for Rent Manager (leave blank to set during import)"
                  disabled={isSaving}
                />
              ) : (
                <Typography
                  className={
                    rmConfig?.default_charge_type ? classes.fieldValue : classes.placeholder
                  }
                >
                  {rmConfig?.default_charge_type || 'Not set'}
                </Typography>
              )}
            </Box>

            {/* Unit Name Mappings */}
            <Box>
              <Typography className={classes.fieldLabel} variant="subtitle2">
                Unit Name Mappings
              </Typography>
              {!isEditing && (
                <Typography className={classes.fieldValue} sx={{ mb: 1 }}>
                  {mappedCount > 0
                    ? `${mappedCount} of ${billableUnits.length} units mapped`
                    : 'No mappings configured'}
                </Typography>
              )}
              {isEditing && (
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>SimpleSub Unit Name</TableCell>
                        <TableCell>Rent Manager Unit Name</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {billableUnits.map((unit) => (
                        <TableRow key={unit.id}>
                          <TableCell>
                            <Typography variant="body2">{unit.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={localUnitMappings[unit.id] || ''}
                              onChange={(e) => handleUnitMappingChange(unit.id, e.target.value)}
                              disabled={isSaving}
                              placeholder={unit.name}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}

        {/* Cancel / Save (in edit mode), matching the ResmanConfigForm pattern. */}
        {isEditing && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 3,
              pt: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <SWLoadingButtonOutlined onClick={handleCancel} disabled={isSaving}>
              Cancel
            </SWLoadingButtonOutlined>
            <SWLoadingButton onClick={handleSave} loading={isSaving}>
              Save
            </SWLoadingButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
